import { createContext, useCallback, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/shared/helpers/cn.helper';

export type ToastTone = 'success' | 'danger' | 'info' | 'warning';

export interface ToastInput {
  readonly title: string;
  readonly description?: string;
  readonly tone?: ToastTone;
}

export interface ToastItem {
  readonly id: number;
  readonly title: string;
  readonly description?: string;
  readonly tone: ToastTone;
}

interface ToastContextValue {
  readonly toast: (input: ToastInput) => void;
  readonly dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_CLASSES: Record<ToastTone, string> = {
  success: 'border-success/25 text-success',
  danger: 'border-danger/25 text-danger',
  info: 'border-info/25 text-info',
  warning: 'border-warning/25 text-warning',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const counterRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (input: ToastInput) => {
      counterRef.current += 1;
      const id = counterRef.current;
      const item: ToastItem = { id, tone: 'info', ...input };
      setItems((current) => [...current.slice(-4), item]);
      window.setTimeout(() => dismiss(id), 5000);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
            <div
              role="region"
              aria-live="polite"
              aria-label="Notifications"
              className="pointer-events-none fixed right-4 bottom-4 z-50 flex w-80 flex-col gap-2"
            >
              {items.map((item) => (
                <div
                  key={item.id}
                  role="status"
                  className={cn(
                    'pointer-events-auto rounded-md border bg-surface-2 px-3.5 py-3 shadow-pop',
                    TONE_CLASSES[item.tone],
                  )}
                >
                  <p className="text-body font-semibold text-ink">{item.title}</p>
                  {item.description ? (
                    <p className="mt-0.5 text-metadata text-ink-muted">{item.description}</p>
                  ) : null}
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

export { ToastContext };
